import React from 'react';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Card,
  CardContent,
  Divider,
  Container,
  Grid,
  Button,
} from '@mui/material';
import { ArticleOutlined as ArticleOutlinedIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AppColors } from '../theme/colors';

const PricingPage: React.FC = () => {
  const navigate = useNavigate();

  const plans = [
    { name: 'プラン50', price: '¥12,000／月（税抜）\n（税込¥13,200）', deviceLimit: '上限端末数：〜50' },
    { name: 'プラン100', price: '¥15,000／月（税抜）\n（税込¥16,500）', deviceLimit: '上限端末数：〜100' },
    { name: 'プラン200', price: '¥24,000／月（税抜）\n（税込¥26,400）', deviceLimit: '上限端末数：〜200' },
    { name: 'プラン300', price: '¥30,000／月（税抜）\n（税込¥33,000）', deviceLimit: '上限端末数：〜300' },
    { name: 'プラン400', price: '¥33,000／月（税抜）\n（税込¥36,300）', deviceLimit: '上限端末数：〜400' },
    { name: 'プラン500', price: '¥36,000／月（税抜）\n（税込¥39,600）', deviceLimit: '上限端末数：〜500' },
  ];

  const buildPlanCard = (plan: typeof plans[0]) => {
    return (
      <Card
        sx={{
          height: 220,
          borderRadius: 0,
          border: `1px solid ${AppColors.border}`,
          borderTop: `8px solid ${AppColors.primary}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          {/* プラン名 */}
          <Box sx={{ textAlign: 'center', mb: 2, px: 2.5, pt: 2.5 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: 20,
                fontWeight: 'bold',
                color: AppColors.textPrimary,
                lineHeight: 1.2,
              }}
            >
              {plan.name}
            </Typography>
          </Box>

          {/* 価格 */}
          <Box sx={{ textAlign: 'center', mb: 2, px: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center' }}>
              {/* メイン価格（緑色） */}
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 'bold',
                  color: AppColors.primary,
                }}
              >
                {plan.price.split('／')[0]}
              </Typography>
              
              {/* /月（税抜）（薄い灰色） */}
              <Typography
                sx={{
                  fontSize: 14,
                  color: AppColors.textSecondary,
                  ml: 0.5,
                }}
              >
                ／月（税抜）
              </Typography>
            </Box>
            
            {/* 税込価格（薄い灰色） */}
            <Typography
              sx={{
                fontSize: 12,
                color: AppColors.textSecondary,
                mt: 0.5,
              }}
            >
              {plan.price.split('\n')[1]}
            </Typography>
          </Box>

          {/* 区切り線 */}
          <hr
            style={{
              height: '0.5px',
              backgroundColor: '#666',
              border: 'none',
              margin: '0 10px 16px 10px',
            }}
          />

          {/* 端末数制限 */}
          <Box sx={{ textAlign: 'center', px: 2.5, pb: 2.5 }}>
            <Typography
              sx={{
                fontSize: 14,
                color: AppColors.textSecondary,
                lineHeight: 1.2,
              }}
            >
              {plan.deviceLimit}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: AppColors.surface }}>
      {/* ヘッダー */}
      <AppBar
        position="fixed"
        sx={{
          bgcolor: AppColors.primary,
          boxShadow: 'none',
          zIndex: 1000,
        }}
      >
        <Toolbar>
          <Typography
            variant="h6"
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: 'white',
            }}
          >
            料金表
          </Typography>
        </Toolbar>
      </AppBar>

      {/* メインコンテンツ */}
      <Box sx={{ pt: 8 }}>
        <Container maxWidth={false} sx={{ maxWidth: 960, px: 2 }}>
          <Box sx={{ p: 2 }}>
            {/* タイトル */}
            <Box sx={{ py: 1.5 }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: 24,
                  fontWeight: 'bold',
                  color: AppColors.textPrimary,
                  mb: 2,
                }}
              >
                LiaVoice 料金プラン
              </Typography>
              <Divider sx={{ height: 1, bgcolor: '#E5E7EB' }} />
            </Box>

            {/* 説明文 */}
            <Typography
              sx={{
                fontSize: 14,
                color: AppColors.textSecondary,
                lineHeight: 1.6,
                mb: 2,
              }}
            >
              LiaVoice（リアボイス）は、従業員が完全匿名で意見や感謝を共有できるアプリです。
              <br />
              心理的安全性を確保しながら、組織の改善に貢献できる環境を提供します。あなたの声が、より良い職場づくりの第一歩となります。
            </Typography>

            {/* プランカード */}
            <Box sx={{ mb: 2 }}>
              {/* 上段: 最初の3つ */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 2,
                mb: 2,
                justifyContent: 'center'
              }}>
                {plans.slice(0, 3).map((plan, index) => (
                  <Box key={index}>
                    {buildPlanCard(plan)}
                  </Box>
                ))}
              </Box>
              
              {/* 下段: 残りの3つ */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 2,
                justifyContent: 'center'
              }}>
                {plans.slice(3, 6).map((plan, index) => (
                  <Box key={index + 3}>
                    {buildPlanCard(plan)}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* CTAセクション */}
            <Box
              sx={{
                mt: 4,
                mb: 3,
                p: { xs: 2.5, md: 3 },
                borderRadius: 2,
                bgcolor: AppColors.surface,
                border: `1px solid ${AppColors.border}`,
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: AppColors.textPrimary,
                  mb: 1,
                }}
              >
                まずはプランを選択してください。
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  color: AppColors.textSecondary,
                  lineHeight: 1.6,
                  mb: 2.5,
                }}
              >
                会社登録を行うと、30日以上の無料期間でそのままご利用いただけます。
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/company-register')}
                sx={{
                  px: 5,
                  py: 1.25,
                  fontSize: 16,
                  fontWeight: 700,
                }}
              >
                このプランで申込む
              </Button>
            </Box>

            {/* 注意事項とリンク */}
            <Box sx={{ mb: 3.75 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  color: AppColors.textSecondary,
                  mb: 0.5,
                  pl: 1.5,
                }}
              >
                ※ 501端末以上はご相談ください（お問い合わせ: info@lefria.com）
              </Typography>
              
              <Button
                startIcon={<ArticleOutlinedIcon sx={{ color: AppColors.primary }} />}
                onClick={() => navigate('/commercial-law')}
                sx={{
                  color: AppColors.primary,
                  fontSize: 14,
                  fontWeight: 600,
                  textTransform: 'none',
                  textDecoration: 'underline',
                  p: 0,
                  minWidth: 'auto',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'underline',
                  },
                }}
              >
                特定商取引法に基づく表記はこちら
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export { PricingPage };
export default PricingPage;
